import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useFeatures } from '../hooks/useFeatures';
import { UpgradePrompt } from '../components/UpgradePrompt';
import { Button } from '../components/ui/button';
import { 
  Bookmark,
  BookmarkSimple,
  Trash,
  SpinnerGap,
  BookOpen
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookmarksPage = () => {
  const { hasFeature, pricing, loading: featuresLoading } = useFeatures();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchBookmarks();
  }, []);

  const fetchBookmarks = async () => {
    try {
      const { data } = await axios.get(`${API}/bookmarks`, { withCredentials: true });
      setBookmarks(data);
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeBookmark = async (questionId) => {
    try {
      await axios.post(`${API}/bookmarks/toggle`, {
        question_id: questionId
      }, { withCredentials: true });
      setBookmarks(prev => prev.filter(b => b.id !== questionId));
    } catch (error) {
      console.error('Failed to remove bookmark:', error);
    }
  };

  if (!featuresLoading && !hasFeature('bookmarks')) {
    return <UpgradePrompt feature="bookmarks" requiredTier="standard" pricing={pricing} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SpinnerGap className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="bookmarks-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Saved Questions
          </p>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">
            Bookmarks
          </h1>
          <p className="text-muted-foreground mt-2">
            {bookmarks.length} saved question{bookmarks.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Empty State */}
        {bookmarks.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-muted rounded-sm flex items-center justify-center mx-auto mb-6">
              <BookmarkSimple className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="font-heading font-bold text-xl mb-2">No bookmarks yet</h2>
            <p className="text-muted-foreground mb-6">
              Save difficult questions during practice to review later
            </p>
            <Link to="/practice">
              <Button className="rounded-sm">
                <BookOpen className="w-5 h-5 mr-2" />
                Start Practicing
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookmarks.map((question) => (
              <div
                key={question.id}
                className="bg-card border border-border rounded-sm overflow-hidden"
                data-testid={`bookmark-${question.id}`}
              >
                <div 
                  className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setExpandedId(expandedId === question.id ? null : question.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                        {question.category}
                      </p>
                      <p className="font-medium">{question.question_text}</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeBookmark(question.id);
                      }}
                      className="p-2 hover:bg-destructive/10 rounded-sm transition-colors text-muted-foreground hover:text-destructive"
                      data-testid={`remove-bookmark-${question.id}`}
                    >
                      <Trash className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedId === question.id && (
                  <div className="border-t border-border p-4 bg-muted/30 animate-fade-in">
                    <div className="space-y-2 mb-4">
                      {question.options.map((option, index) => (
                        <div
                          key={index}
                          className={cn(
                            "p-3 rounded-sm border text-sm",
                            index === question.correct_answer
                              ? "border-success bg-success/10 text-success"
                              : "border-border"
                          )}
                        >
                          <span className="font-bold mr-2">{String.fromCharCode(65 + index)}.</span>
                          {option}
                        </div>
                      ))}
                    </div>
                    <div className="p-3 bg-card border border-border rounded-sm">
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
                        Explanation
                      </p>
                      <p className="text-sm">{question.explanation}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
